import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import AppointmentPicker from "@/components/landing/AppointmentPicker";

const SECTORS = [
  "İmalat & Üretim",
  "Perakende & E-Ticaret",
  "Lojistik & Depolama",
  "Hizmet & Danışmanlık",
  "İnşaat & Gayrimenkul",
  "Gıda & Tarım",
  "Teknoloji & Yazılım",
  "Diğer",
];

const EMPLOYEE_RANGES = ["1 - 10", "11 - 50", "51 - 250", "250+"];

const COMPANY_TYPES = ["Şahıs Şirketi", "Limited Şirketi", "Anonim Şirketi", "Diğer"];

const REQUIRED_FIELDS = [
  "full_name",
  "company_name",
  "phone",
  "email",
  "company_type",
  "tax_number",
  "foundation_year",
  "city_district",
  "sector",
  "employee_count",
  "description",
];

const initialForm = {
  full_name: "",
  company_name: "",
  phone: "",
  email: "",
  company_type: "",
  tax_number: "",
  foundation_year: "",
  city_district: "",
  nace_code: "",
  sector: "",
  employee_count: "",
  description: "",
};

const inputClass =
  "w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10";

function Field({ label, error, testId, children }) {
  return (
    <div data-testid={testId}>
      <label className="block text-sm font-semibold text-[#0F172A] mb-2">{label}</label>
      {children}
      {error && (
        <p data-testid={`${testId}-error`} className="mt-1.5 text-xs font-medium text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

export default function AnalysisForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [agreements, setAgreements] = useState({ kvkk: false, membership: false });

  const progress = useMemo(
    () =>
      Math.round(
        (REQUIRED_FIELDS.filter((k) => String(form[k]).trim() !== "").length / REQUIRED_FIELDS.length) * 100
      ),
    [form]
  );

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Lütfen adınızı ve soyadınızı girin.";
    if (form.company_name.trim().length < 2) e.company_name = "Lütfen firma adını girin.";
    if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Lütfen geçerli bir telefon numarası girin.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Lütfen geçerli bir e-posta adresi girin.";
    if (!form.company_type) e.company_type = "Lütfen şirket türünü seçin.";
    if (!/^\d{10,11}$/.test(form.tax_number.replace(/\s/g, ""))) e.tax_number = "Lütfen 10-11 haneli vergi numaranızı girin.";
    const year = parseInt(form.foundation_year, 10);
    if (!/^\d{4}$/.test(form.foundation_year) || year < 1900 || year > new Date().getFullYear())
      e.foundation_year = "Lütfen geçerli bir kuruluş yılı girin (örn. 2008).";
    if (form.city_district.trim().length < 3) e.city_district = "Lütfen il ve ilçenizi girin (örn. İstanbul / Kadıköy).";
    if (!form.sector) e.sector = "Lütfen sektörünüzü seçin.";
    if (!form.employee_count) e.employee_count = "Lütfen çalışan sayısı aralığını seçin.";
    if (form.description.trim().length < 10) e.description = "Lütfen ihtiyacınızı kısaca açıklayın (en az 10 karakter).";
    if (!agreements.kvkk) e.kvkk = "Devam etmek için KVKK Aydınlatma Metni'ni onaylamanız gerekir.";
    if (!agreements.membership) e.membership = "Devam etmek için Üyelik Sözleşmesi'ni onaylamanız gerekir.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error("Lütfen formdaki eksik alanları tamamlayın.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/submissions", form);
      setResult(data);
      toast.success("Başvurunuz başarıyla alındı.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-white rounded-3xl border border-[#E2E8F0] p-8 sm:p-12 text-center shadow-2xl shadow-[#0F172A]/5"
        data-testid="form-success-view"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-[#0D9488]/10 grid place-items-center">
          <CheckCircle2 size={36} className="text-[#0D9488]" />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0F172A] mt-6">Başvurunuz Alındı!</h3>
        <p className="text-[#475569] mt-3 max-w-md mx-auto">
          Ön analiz talebiniz ekibimize iletildi. Danışmanlarımız 24 saat içinde sizinle iletişime geçecek.
        </p>
        <div className="mt-6 inline-flex flex-col items-center gap-1 rounded-2xl border border-dashed border-[#93C5FD] bg-[#0000FF]/5 px-8 py-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#0000FF]">Referans Kodunuz</span>
          <span className="font-mono text-2xl font-semibold text-[#0F172A]" data-testid="success-reference-code">
            {result.reference_code}
          </span>
        </div>
        <AppointmentPicker referenceCode={result.reference_code} />
        <div className="mt-8">
          <button
            onClick={() => {
              setResult(null);
              setForm(initialForm);
            }}
            data-testid="new-submission-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-6 py-3 text-sm font-semibold text-[#0F172A] transition-colors hover:border-[#0000FF] hover:text-[#0000FF]"
          >
            <RotateCcw size={16} />
            Yeni Başvuru Oluştur
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-3xl border border-[#E2E8F0] p-6 sm:p-10 shadow-2xl shadow-[#0F172A]/5"
      data-testid="analysis-form"
      noValidate
    >
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#475569]">Form İlerlemesi</span>
          <span className="font-mono text-xs font-semibold text-[#0000FF]" data-testid="form-progress-label">%{progress}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#E2E8F0] overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-[#0000FF]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            data-testid="form-progress-bar"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Ad Soyad" error={errors.full_name} testId="field-full-name">
          <input
            type="text"
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            placeholder="Örn. Ahmet Yılmaz"
            className={inputClass}
            data-testid="input-full-name"
          />
        </Field>
        <Field label="Firma Adı" error={errors.company_name} testId="field-company-name">
          <input
            type="text"
            value={form.company_name}
            onChange={(e) => set("company_name", e.target.value)}
            placeholder="Örn. Yılmaz Lojistik A.Ş."
            className={inputClass}
            data-testid="input-company-name"
          />
        </Field>
        <Field label="Telefon" error={errors.phone} testId="field-phone">
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="0 (5XX) XXX XX XX"
            className={inputClass}
            data-testid="input-phone"
          />
        </Field>
        <Field label="E-posta" error={errors.email} testId="field-email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="ahmet@yilmazlojistik.com"
            className={inputClass}
            data-testid="input-email"
          />
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Şirket Türü" error={errors.company_type} testId="field-company-type">
          <div className="flex flex-wrap gap-2" data-testid="input-company-type">
            {COMPANY_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => set("company_type", type)}
                data-testid={`company-type-option-${type.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all active:scale-[0.97] ${
                  form.company_type === type
                    ? "border-[#0000FF] bg-[#0000FF] text-white shadow-lg shadow-[#0000FF]/25"
                    : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#93C5FD] hover:text-[#0000FF]"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        <Field label="Vergi Numarası" error={errors.tax_number} testId="field-tax-number">
          <input
            type="text"
            inputMode="numeric"
            value={form.tax_number}
            onChange={(e) => set("tax_number", e.target.value)}
            placeholder="10-11 haneli vergi numarası"
            className={inputClass}
            data-testid="input-tax-number"
          />
        </Field>
        <Field label="NACE Kodu (Opsiyonel)" error={errors.nace_code} testId="field-nace-code">
          <input
            type="text"
            value={form.nace_code}
            onChange={(e) => set("nace_code", e.target.value)}
            placeholder="Örn. 49.41"
            className={inputClass}
            data-testid="input-nace-code"
          />
        </Field>
        <Field label="Kuruluş Yılı" error={errors.foundation_year} testId="field-foundation-year">
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={form.foundation_year}
            onChange={(e) => set("foundation_year", e.target.value)}
            placeholder="Örn. 2008"
            className={inputClass}
            data-testid="input-foundation-year"
          />
        </Field>
        <Field label="İl - İlçe" error={errors.city_district} testId="field-city-district">
          <input
            type="text"
            value={form.city_district}
            onChange={(e) => set("city_district", e.target.value)}
            placeholder="Örn. İstanbul / Kadıköy"
            className={inputClass}
            data-testid="input-city-district"
          />
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Sektör" error={errors.sector} testId="field-sector">
          <div className="flex flex-wrap gap-2" data-testid="input-sector">
            {SECTORS.map((sector) => (
              <button
                key={sector}
                type="button"
                onClick={() => set("sector", sector)}
                data-testid={`sector-option-${sector.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition-all active:scale-[0.97] ${
                  form.sector === sector
                    ? "border-[#0000FF] bg-[#0000FF] text-white shadow-lg shadow-[#0000FF]/25"
                    : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#93C5FD] hover:text-[#0000FF]"
                }`}
              >
                {sector}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-6">
        <Field label="Çalışan Sayısı" error={errors.employee_count} testId="field-employee-count">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="input-employee-count">
            {EMPLOYEE_RANGES.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => set("employee_count", range)}
                data-testid={`employee-option-${range.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                className={`rounded-xl border px-4 py-3 text-sm font-semibold transition-all active:scale-[0.97] ${
                  form.employee_count === range
                    ? "border-[#0D9488] bg-[#0D9488]/5 text-[#0D9488] ring-2 ring-[#0D9488]/20"
                    : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#0D9488]/50"
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-6">
        <Field label="İhtiyaç / Talep Açıklaması" error={errors.description} testId="field-description">
          <textarea
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Firmanızın mevcut dijital durumunu, karşılaştığınız zorlukları veya gerçekleştirmek istediğiniz hedefleri kısaca açıklayınız..."
            rows={5}
            className={`${inputClass} resize-none`}
            data-testid="input-description"
          />
        </Field>
      </div>

      <div className="mt-6 space-y-3" data-testid="agreements-block">
        <div>
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="kvkk-agreement">
            <span
              className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border grid place-items-center transition-all ${
                agreements.kvkk ? "bg-[#0000FF] border-[#0000FF]" : "bg-white border-[#E2E8F0] group-hover:border-[#93C5FD]"
              }`}
            >
              {agreements.kvkk && (
                <svg viewBox="0 0 12 10" className="w-3 h-3 fill-white"><path d="M1 5.5 4.2 8.5 11 1.5 9.8.3 4.1 6 2.2 4.3z" /></svg>
              )}
            </span>
            <input
              type="checkbox"
              checked={agreements.kvkk}
              onChange={() => {
                setAgreements((a) => ({ ...a, kvkk: !a.kvkk }));
                setErrors((e) => ({ ...e, kvkk: undefined }));
              }}
              className="sr-only"
              data-testid="kvkk-checkbox"
            />
            <span className="text-xs leading-relaxed text-[#475569]">
              Kişisel verilerimin işlenmesine ilişkin{" "}
              <a href="https://www.kobi-line.com.tr/gizlilik-politikasi" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0000FF] hover:underline" data-testid="kvkk-link" onClick={(e) => e.stopPropagation()}>
                KVKK Aydınlatma Metni / Gizlilik Politikası
              </a>
              'nı okudum, onaylıyorum.
            </span>
          </label>
          {errors.kvkk && <p className="mt-1.5 ml-8 text-xs font-medium text-red-500" data-testid="kvkk-error">{errors.kvkk}</p>}
        </div>
        <div>
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="membership-agreement">
            <span
              className={`mt-0.5 shrink-0 w-5 h-5 rounded-md border grid place-items-center transition-all ${
                agreements.membership ? "bg-[#0000FF] border-[#0000FF]" : "bg-white border-[#E2E8F0] group-hover:border-[#93C5FD]"
              }`}
            >
              {agreements.membership && (
                <svg viewBox="0 0 12 10" className="w-3 h-3 fill-white"><path d="M1 5.5 4.2 8.5 11 1.5 9.8.3 4.1 6 2.2 4.3z" /></svg>
              )}
            </span>
            <input
              type="checkbox"
              checked={agreements.membership}
              onChange={() => {
                setAgreements((a) => ({ ...a, membership: !a.membership }));
                setErrors((e) => ({ ...e, membership: undefined }));
              }}
              className="sr-only"
              data-testid="membership-checkbox"
            />
            <span className="text-xs leading-relaxed text-[#475569]">
              <a href="https://www.kobi-line.com.tr/uyelik-sozlesmesi" target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0000FF] hover:underline" data-testid="membership-link" onClick={(e) => e.stopPropagation()}>
                Üyelik Sözleşmesi
              </a>
              'ni okudum, kabul ediyorum.
            </span>
          </label>
          {errors.membership && <p className="mt-1.5 ml-8 text-xs font-medium text-red-500" data-testid="membership-error">{errors.membership}</p>}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="submit-analysis-button"
        className="mt-8 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-[#0000FF] text-white font-semibold px-7 py-4 shadow-xl shadow-[#0000FF]/25 transition-all hover:bg-[#0000D6] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Gönderiliyor...
          </>
        ) : (
          <>
            Ücretsiz Ön Analiz Talebini Gönder
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="mt-4 text-center text-xs text-[#94A3B8]">
        Bilgileriniz yalnızca ön analiz amacıyla kullanılır. KVKK kapsamında korunur.
      </p>
    </motion.form>
  );
}
